import alt from "../alt";
import indexBy from "lodash/keyBy";
import EventFetcher from "../utils/EventFetcher";
import EventActions from "../actions/EventActions";
import PersonStore from "../stores/PersonStore";
import FilmActions from "../actions/FilmActions";
import PassActions from "../actions/PassActions";
import TicketActions from "../actions/TicketActions";

class EventStore {
  constructor() {
    this.bindActions(EventActions);

    this.state = {
      events: {},
      films: {},
      marquee: null,
      isFetchingAll: false,
      isFetchingAllPast: false,
      isFetchingMarquee: false,
      hasFetchedAll: false,
      hasFetchedAllWithPast: false,
      fetchAllNoOverwriteId: null,
      forceNoAllOverwriteId: null
    };

    this.exportPublicMethods({
      getEventById: this.getEventById.bind(this),
      getFilmById: this.getFilmById.bind(this),
      getTicketBucketWithEventById:
        this.getTicketBucketWithEventById.bind(this),
      getDefaultTicketBucketWithEventByEventId:
        this.getDefaultTicketBucketWithEventByEventId.bind(this)
    });
  }

  getEventById(id) {
    return this.state.events[id];
  }

  getFilmById(id) {
    return this.state.films[id];
  }

  getTicketBucketWithEventById(ticketBucketId) {
    let event = Object.values(this.state.events).find(e =>
      e.ticket_buckets.some(b => b.id === ticketBucketId)
    );
    if (event) {
      let bucket = Object.assign(
        {},
        event.ticket_buckets.find(b => b.id === ticketBucketId)
      );
      bucket.event = event;
      return bucket;
    }
    return null;
  }

  getDefaultTicketBucketWithEventByEventId(eventId) {
    let event = Object.values(this.state.events).find(e => e.id === eventId);
    if (event) {
      let bucket = Object.assign(
        {},
        event.ticket_buckets.length === 1
          ? event.ticket_buckets[0]
          : event.ticket_buckets.find(
              b =>
                b.name === "General Admission" ||
                b.name === "Single Film Ticket" || // sundance
                b.name === "Virtual Admission" // sundance
            )
      );
      if (!bucket.id) {
        return null;
      }
      bucket.event = event;
      return bucket;
    }
    return null;
  }

  onFetchAll(opts) {
    let { includePastEvents, includeVirtualEvents, eventIds } = opts || {};

    // Don't run more than one fetch at once
    if (!includePastEvents && this.state.isFetchingAll) {
      return;
    }
    if (includePastEvents && this.state.isFetchingAllPast) {
      return;
    }
    this.setState({
      isFetchingAll: !includePastEvents,
      isFetchingAllPast: includePastEvents
    });

    EventFetcher.fetchAll(PersonStore.getState().token, {
      includePastEvents,
      includeVirtualEvents,
      eventIds
    }).then(({ events, includes_all_events }) => {
      let indexedEvents = indexBy(events, "id");

      if (this.state.fetchAllNoOverwriteId) {
        delete indexedEvents[this.state.fetchAllNoOverwriteId];
      }
      if (
        this.state.forceNoAllOverwriteId &&
        Date.now() - this.state.forceNoAllOverwriteTime <= 20 * 1000
      ) {
        // After 20 seconds, automatically expire any overwrite time limit because we assume that it's
        // finished the fetch by now (please? pretty please?)
        delete indexedEvents[this.state.forceNoAllOverwriteId];
      }

      EventActions.updateEvents(indexedEvents);
      this.setState({
        fetchAllNoOverwriteId: null,
        isFetchingAll: !includePastEvents ? false : undefined,
        isFetchingAllPast: includePastEvents ? false : undefined,
        hasFetchedAll: true,
        hasFetchedAllWithPast:
          this.state.hasFetchedAllWithPast || includes_all_events
      });
    });

    // When events are fetched, we automatically fetch passes and tickets as well so that the buttons can be
    // nice and responsive!
    if (PersonStore.isLoggedIn()) {
      PassActions.fetchAll.defer({ firstTimeOnly: true });
      TicketActions.fetchAll.defer();
    }
  }

  onFetchAllForFilm(filmId) {
    this.setState({ isFetchingAllForFilm: true });
    EventFetcher.fetchAllForFilm(PersonStore.getState().token, filmId).then(
      ({ events }) => {
        this.setState({ isFetchingAllForFilm: false });
        EventActions.updateEvents(indexBy(events, "id"));
      }
    );
  }

  onFetchOne({ id, options }) {
    // If a current fetchAll is in progress, make sure it doesn't overwrite
    // the event that we're solo-fetching currently.
    if (this.state.isFetchingAll || this.state.isFetchingAllPast) {
      this.setState({ fetchAllNoOverwriteId: id });
    }

    if (options && options.forceNoAllOverwrite) {
      this.setState({
        forceNoAllOverwriteId: id,
        forceNoAllOverwriteTime: Date.now()
      });
    }

    EventFetcher.fetchOne(PersonStore.getState().token, id).then(data => {
      EventActions.updateEvent(id, data);
    });
  }

  onClearNoAllOverwrite() {
    this.setState({ forceNoAllOverwriteId: null });
  }

  onFetchMarquee(opts) {
    if (opts && opts.firstTimeOnly && this.state.marquee) {
      return;
    }

    this.setState({ isFetchingMarquee: true });
    EventFetcher.fetchMarquee(opts.date).then(
      ({ films, standalone_events, shows_by_day }) => {
        this.setState({
          isFetchingMarquee: false,
          marquee: {
            standaloneEvents: standalone_events,
            showsByDay: shows_by_day
          }
        });
        FilmActions.updateFilms.defer(indexBy(films, "id"));
      }
    );

    // When events are fetched, we automatically fetch passes and tickets as well so that the buttons can be
    // nice and responsive!
    if (PersonStore.isLoggedIn()) {
      PassActions.fetchAll.defer({ firstTimeOnly: true });
      TicketActions.fetchAll.defer();
    }
  }

  onUpdateEvents(events) {
    let updated = Object.assign({}, this.state.events, events);

    // Populate films
    let films = Object.assign({}, this.state.films);
    Object.values(events).map(e => {
      const event = { ...e };
      delete event.films;
      e.films.forEach(f => {
        if (films[f.id]) {
          if (!films[f.id].events.some(filmEvent => filmEvent.id === e.id)) {
            films[f.id].events.push(event);
          }
        } else {
          films[f.id] = EventFetcher.processFilm(f);
          films[f.id].events = [event];
        }

        // Inject the tags
        f.tags.forEach(filmTag => {
          if (!e.tags.some(t => t.id === filmTag.id)) {
            filmTag.type = "film";
            e.tags.push(filmTag);
          }
        });
      });
    });

    // Dispatch film update
    FilmActions.updateFilms.defer(films);

    this.setState({ events: updated });
  }

  onUpdateEvent(data) {
    EventActions.updateEvents.defer(
      indexBy(
        [data.event].concat(
          (data.event._other_events || []).filter(e => !this.state.events[e.id])
        ),
        "id"
      )
    );
  }
}

export default alt.createStore(EventStore, "EventStore");



// WEBPACK FOOTER //
// ./src/app/stores/EventStore.js